import { c as _c } from "react/compiler-runtime";
import type { Iso3166TwoDigitCountryCode, OnboardingRequirementCollectDocument, OnboardingSessionConfig } from '@onefootprint/request-types';
import { createContext, useContext, useReducer } from 'react';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import type { CameraErrorCause, IdDocImageErrorType, ReceivedImagePayload } from '../../types';
import { isDenied, isGranted, isMobileKind, isPrompt } from '../../utils/capture';
export type NonIdDocStep = 'init' | 'documentPrompt' | 'mobileRequestCameraAccess' | 'mobileCameraAccessDenied' | 'mobileCameraErrored' | 'mobileImageCapture' | 'mobileRetry' | 'desktopRetry' | 'mobileProcessing' | 'desktopProcessing' | 'complete' | 'failure';
export type NonIdDocCtx = {
  authToken: string;
  cameraPermissionState: PermissionState;
  cameraErrorCause?: CameraErrorCause;
  device: DeviceInfo;
  config: OnboardingSessionConfig;
  requirement: OnboardingRequirementCollectDocument;
  allowPdf: boolean;
  document?: ReceivedImagePayload;
  id?: string;
  errors?: IdDocImageErrorType[];
  hasBadConnectivity?: boolean;
  obConfigSupportedCountries?: Iso3166TwoDigitCountryCode[];
};
export type NonIdDocState = {
  value: NonIdDocStep;
  context: NonIdDocCtx;
};
export type NonIdDocAction = {
  type: 'cameraAccessDenied';
  payload: {
    status: PermissionState;
  };
} | {
  type: 'cameraAccessGranted';
  payload: {
    status: PermissionState;
  };
} | {
  type: 'cameraErrored';
  payload: {
    cause: CameraErrorCause;
  };
} | {
  type: 'cameraStuck';
} | {
  type: 'contextInitialized';
  payload: {
    id: string;
  };
} | {
  type: 'contextInitializationFailed';
} | {
  type: 'navigatedToPrev';
} | {
  type: 'navigatedToPrompt';
} | {
  type: 'processingErrored';
  payload: {
    errors: IdDocImageErrorType[];
  };
} | {
  type: 'processingSucceeded';
} | {
  type: 'receivedDocument';
  payload: ReceivedImagePayload;
} | {
  type: 'retryLimitExceeded';
} | {
  type: 'startImageCapture';
} | {
  type: 'uploadErrored';
  payload: {
    errors: IdDocImageErrorType[];
  };
};
const nonIdDocReducer = (state: NonIdDocState, action: NonIdDocAction): NonIdDocState => {
  const {
    value: step,
    context: ctx
  } = state;
  const isMobile = isMobileKind(ctx.device.type);
  switch (action.type) {
    case 'contextInitialized':
      {
        if (step !== 'init') return state;
        return {
          value: 'documentPrompt',
          context: {
            ...ctx,
            id: action.payload.id
          }
        };
      }
    case 'contextInitializationFailed':
      {
        return {
          value: 'failure',
          context: ctx
        };
      }
    case 'receivedDocument':
      {
        const newCtx: NonIdDocCtx = {
          ...ctx,
          document: {
            imageFile: action.payload.imageFile,
            captureKind: action.payload.captureKind,
            extraCompressed: action.payload.extraCompressed,
            forcedUpload: action.payload.forcedUpload
          }
        };
        if (step === 'documentPrompt' || step === 'mobileRetry' || step === 'mobileImageCapture') {
          if (isMobile) return {
            value: 'mobileProcessing',
            context: newCtx
          };
          return {
            value: 'desktopProcessing',
            context: newCtx
          };
        }
        if (step === 'desktopRetry') {
          return {
            value: 'desktopProcessing',
            context: {
              ...newCtx,
              errors: undefined
            }
          };
        }
        return state;
      }
    case 'startImageCapture':
      {
        if (step !== 'documentPrompt' && step !== 'mobileRetry') return state;
        if (isPrompt(ctx.cameraPermissionState)) {
          return {
            value: 'mobileRequestCameraAccess',
            context: ctx
          };
        }
        if (isDenied(ctx.cameraPermissionState)) {
          return {
            value: 'mobileCameraAccessDenied',
            context: ctx
          };
        }
        return {
          value: 'mobileImageCapture',
          context: ctx
        };
      }
    case 'navigatedToPrev':
      {
        if (step === 'mobileRequestCameraAccess') return {
          value: 'documentPrompt',
          context: ctx
        };
        if (step === 'mobileCameraAccessDenied') return {
          value: 'mobileRequestCameraAccess',
          context: ctx
        };
        if (step === 'mobileCameraErrored') return {
          value: 'documentPrompt',
          context: ctx
        };
        if (step === 'mobileImageCapture') return {
          value: 'documentPrompt',
          context: ctx
        };
        return state;
      }
    case 'navigatedToPrompt':
      {
        const newCtx: NonIdDocCtx = {
          ...ctx,
          errors: undefined
        };
        return {
          value: 'documentPrompt',
          context: newCtx
        };
      }
    case 'cameraAccessGranted':
      {
        if (step !== 'mobileRequestCameraAccess') return state;
        const newCtx: NonIdDocCtx = {
          ...ctx,
          cameraPermissionState: action.payload.status
        };
        if (isGranted(action.payload.status)) {
          return {
            value: 'mobileImageCapture',
            context: newCtx
          };
        }
        return {
          value: 'documentPrompt',
          context: newCtx
        };
      }
    case 'cameraAccessDenied':
      {
        const newCtx: NonIdDocCtx = {
          ...ctx,
          cameraPermissionState: action.payload.status
        };
        return {
          value: 'mobileCameraAccessDenied',
          context: newCtx
        };
      }
    case 'cameraErrored':
      {
        const newCtx: NonIdDocCtx = {
          ...ctx,
          cameraErrorCause: action.payload.cause
        };
        return {
          value: 'mobileCameraErrored',
          context: newCtx
        };
      }
    case 'cameraStuck':
      {
        if (step === 'mobileImageCapture') return {
          value: 'documentPrompt',
          context: ctx
        };
        return state;
      }
    case 'processingSucceeded':
      {
        return {
          value: 'complete',
          context: ctx
        };
      }
    case 'processingErrored':
      {
        if (step !== 'mobileProcessing' && step !== 'desktopProcessing') return state;
        const hasBadConnectivity = action.payload.errors.some(e => e.errorType === 'network_error') || ctx.hasBadConnectivity;
        const newCtx: NonIdDocCtx = {
          ...ctx,
          errors: action.payload.errors,
          document: undefined,
          hasBadConnectivity
        };
        if (step === 'mobileProcessing') return {
          value: 'mobileRetry',
          context: newCtx
        };
        return {
          value: 'desktopRetry',
          context: newCtx
        };
      }
    case 'uploadErrored':
      {
        const newCtx: NonIdDocCtx = {
          ...ctx,
          errors: action.payload.errors
        };
        return {
          value: 'desktopRetry',
          context: newCtx
        };
      }
    case 'retryLimitExceeded':
      {
        return {
          value: 'failure',
          context: ctx
        };
      }
    default:
      return state;
  }
};
type NonIdDocContextValue = [NonIdDocState, React.Dispatch<NonIdDocAction>];
const NonIdDocCtx = createContext<NonIdDocContextValue | undefined>(undefined);
type NonIdDocProviderProps = {
  initialContext: NonIdDocCtx;
  children: React.ReactNode;
};
const NonIdDocProvider = t0 => {
  const $ = _c(7);
  const {
    initialContext,
    children
  } = t0;
  let t1;
  if ($[0] !== initialContext) {
    t1 = {
      value: "init",
      context: initialContext
    };
    $[0] = initialContext;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [state, dispatch] = useReducer(nonIdDocReducer, t1);
  let t2;
  if ($[2] !== state) {
    t2 = [state, dispatch];
    $[2] = state;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t2) {
    t3 = <NonIdDocCtx.Provider value={t2}>{children}</NonIdDocCtx.Provider>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const useNonIdDocMachine = () => {
  const ctx = useContext(NonIdDocCtx);
  if (!ctx) {
    throw new Error("useNonIdDocMachine must be used inside NonIdDocProvider");
  }
  return ctx;
};
export { NonIdDocProvider, useNonIdDocMachine };